import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminPatrolSystemMapping, UpsertAdminPatrolSystemMappingBody } from "./types/patrolSystemMappings";

export const usePatrolSystemMappingsApi = () => {
  const api = useApi();

  const getAdminPatrolSystemMapping = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/system_mappings`;
      return useSWRMutation<AdminPatrolSystemMapping, CustomError, string, string>(
        url,
        (url, { arg }: { arg: string }) => api.get<AdminPatrolSystemMapping>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminPatrolSystemMappings = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/systems/${systemId}/system_mappings`, query);
      return useSWR<Paginated<AdminPatrolSystemMapping>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminPatrolSystemMapping>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminPatrolSystemMapping = useCallback(
    async (body: UpsertAdminPatrolSystemMappingBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/system_mappings`;
      const id = await api.post<{ id: string }, UpsertAdminPatrolSystemMappingBody>(url, body);
      mutateCache([url, `/admin/settings/patrol/systems/${body.systemId}/system_mappings`]);
      return id;
    },
    [api],
  );

  const updateAdminPatrolSystemMapping = useCallback(
    async (patrolSystemId: string, body: UpsertAdminPatrolSystemMappingBody): Promise<void> => {
      const url = `/admin/settings/patrol/system_mappings/${patrolSystemId}`;
      (await api.put<UpsertAdminPatrolSystemMappingBody>(url, body),
        mutateCache([
          "/admin/settings/patrol/system_mappings",
          `/admin/settings/patrol/systems/${body.systemId}/system_mappings`,
        ]));
    },
    [api],
  );

  const deleteAdminPatrolSystemMapping = useCallback(
    async (systemId: string, patrolSystemMappingId: string): Promise<void> => {
      const url = `/admin/settings/patrol/system_mappings/${patrolSystemMappingId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/patrol/system_mappings`,
        `/admin/settings/patrol/systems/${systemId}/system_mappings`,
      ]);
    },
    [api],
  );

  return {
    getAdminPatrolSystemMapping,
    useAdminPatrolSystemMappings,
    insertAdminPatrolSystemMapping,
    updateAdminPatrolSystemMapping,
    deleteAdminPatrolSystemMapping,
  };
};
